/**
 * FigmaDomesticBoard — domestic/special/international match list
 * Figma 445:8998 content area
 */
import React, { useEffect, useState } from 'react';
import TeamLogo from '../TeamLogo';
import getSportIconUrl from './sportIconResolver';
import getCountryFlagUrl from './countryFlagResolver';
import { buildMarketDisplayTitle } from './marketTitleResolver';
import { getSelectionLabel } from './selectionLabelResolver';
import { SportsListSkeleton } from '../../../components/Skeleton/Skeleton';
import { hasSportsMatchStartElapsed } from '../_helpers/sportsMatchTime';

const normalizeSelectionName = (value) => String(value || '').trim().toLowerCase();

// Invert a single numeric handicap line for the opposing side (-0.5 -> +0.5).
// Split/quarter lines ("-0.5, -1") or non-numeric values are left untouched —
// there's no safe single-token inversion for those, and the shared raw line
// was already the previous behavior for them.
const invertLine = (line) => {
  const n = parseFloat(line);
  if (Number.isNaN(n) || !/^[+-]?\d+(\.\d+)?$/.test(String(line).trim())) return line;
  const inverted = -n;
  return inverted > 0 ? `+${inverted}` : `${inverted}`;
};

// Append a line value to a selection label unless it's already spelled out —
// HC selection names from the feed sometimes already read "Team -0.5", so
// blindly appending would duplicate it as "Team -0.5 (-0.5)".
const withHandicapLineSuffix = (label, line) => {
  const clean = String(line || '').trim();
  if (!clean || String(label).includes(clean)) return label;
  return `${label} (${clean})`;
};

// Over/Under labels read "Over 9.5" — no parens — matching feed names that
// already bake the line in (e.g. "오버 2.5"); only the '오버'/'언더' fallback
// actually needs the line appended.
const withOverUnderLineSuffix = (label, line) => {
  const clean = String(line || '').trim();
  if (!clean || String(label).includes(clean)) return label;
  return `${label} ${clean}`;
};

const MAIN_OUTCOME_ALIASES = {
  home: new Set(['1', 'home', 'h', 'home team', '홈', '홈승']),
  draw: new Set(['x', 'draw', 'd', '무', '무승부']),
  away: new Set(['2', 'away', 'a', 'away team', '원정', '원정승']),
};

const findMainOutcomeOdds = (oddsList, outcome) => {
  const odds = Array.isArray(oddsList) ? oddsList : [];
  const aliases = MAIN_OUTCOME_ALIASES[outcome];
  const found = odds.find((selection) => {
    const name = normalizeSelectionName(selection?.name);
    const nameKo = normalizeSelectionName(selection?.name_ko);
    return aliases.has(name) || aliases.has(nameKo);
  });
  if (found) return found;
  if (outcome === 'home') return odds[0] || null;
  if (outcome === 'away') return odds.length >= 2 ? odds[odds.length - 1] : null;
  return null;
};

const FigmaDomesticBoard = ({
  effectiveBonusTiers, activeBonusFold,
  groupedByLeague, loading, matches,
  isSelected, handleOddsClick, oddsFlash, oddsLatest,
  formatOdds, formatDelta, formatTime, _fmt,
  getSportIcon, isDomestic, getMarkets, OddsBlock,
  goToPage, currentPage, totalPages, hasMoreMatches, hasPrevPage, matchTotal,
  matchCounts, liveCount, bettingType,
  isMobile,
}) => {
  const preferGenericTeamLabels = bettingType === 'DOMESTIC' || bettingType === 'SPECIAL';
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 10000);
    return () => window.clearInterval(timerId);
  }, []);

  const shouldLockStartedPrematch = (match) => !match?.is_live && hasSportsMatchStartElapsed(match, nowMs);

  const resolveTeamLogoUrl = (match, side) => (
    match?.[`${side}_team_logo_url`]
    || match?.[`${side}_team_logo`]
    || match?.[`${side}_logo_url`]
    || match?.[`${side}_logo`]
    || match?.[`${side}TeamLogoUrl`]
    || match?.[`${side}TeamLogo`]
    || null
  );

  const renderOddsBtn = (odds, match, market, label) => {
    if (!odds) {
      return (
        <div className="zxsb_odds_btn zxsb_odds_empty">
          <span className="zxsb_odds_label">{label || '-'}</span>
          <span className="zxsb_odds_val">-</span>
        </div>
      );
    }
    const flash = oddsFlash[odds.id];
    const sus = shouldLockStartedPrematch(match) || (oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended);
    return (
      <button type="button"
        className={`zxsb_odds_btn ${isSelected(odds.id) ? 'zxsb_odds_sel' : ''} ${sus ? 'zxsb_odds_sus' : ''} ${flash ? 'zxsb_flash_' + flash.direction : ''}`}
        onClick={(e) => { e.stopPropagation(); !sus && handleOddsClick(match, market, odds); }}
        disabled={sus}>
        <span className="zxsb_odds_label">{label}</span>
        <span className="zxsb_odds_val">
          {formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt)}
        </span>
        {flash && <span className={`zxsb_delta zxsb_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  const renderMobileOddsBtn = (odds, match, market, label) => {
    if (!odds) {
      return (
        <div className="zxsb_mo_btn zxsb_mo_btn_empty">
          <span className="zxsb_mo_btn_label">{label || '-'}</span>
          <span className="zxsb_mo_btn_value">-</span>
        </div>
      );
    }
    const flash = oddsFlash[odds.id];
    const sus = shouldLockStartedPrematch(match) || (oddsLatest[odds.id]?.is_suspended ?? odds.is_suspended);
    return (
      <button
        type="button"
        className={`zxsb_mo_btn ${isSelected(odds.id) ? 'zxsb_mo_btn_sel' : ''} ${sus ? 'zxsb_mo_btn_sus' : ''} ${flash ? `zxsb_flash_${flash.direction}` : ''}`}
        onClick={(e) => { e.stopPropagation(); !sus && handleOddsClick(match, market, odds); }}
        disabled={sus}
      >
        <span className="zxsb_mo_btn_label">{label}</span>
        <span className="zxsb_mo_btn_value">
          {formatOdds(oddsLatest[odds.id]?.odds ?? odds.odds, _fmt)}
        </span>
        {flash && <span className={`zxsb_delta zxsb_delta_${flash.direction}`}>{flash.direction === 'up' ? '+' : '-'}{formatDelta(flash.delta, _fmt)}</span>}
      </button>
    );
  };

  const resolveSelectionLabel = (odds, match, market, fallback) => (
    getSelectionLabel(odds, { market, match, fallback, preferGenericTeamLabels })
  );

  const renderMobileStaticCell = (value, className = '') => (
    <div className={`zxsb_mo_static ${className}`.trim()}>
      <span className="zxsb_mo_static_value">{value || '-'}</span>
    </div>
  );

  const splitMarketLabel = (marketLabel) => {
    const text = String(marketLabel || '-').trim();
    const bracketMatch = text.match(/^(.+?)\s*(\[[^\]]+\])$/);
    const parenMatch = text.match(/^(.+?)\s*(\([^)]+\))$/);
    const periodMatch = text.match(/^(.+?)\s+(전반전|후반전|연장포함|1쿼터|2쿼터|3쿼터|4쿼터|1세트|2세트|3세트|4세트|5세트|1피리어드|2피리어드|3피리어드|4피리어드|홈팀|원정팀)$/);
    const [head, tail] = bracketMatch
      ? [bracketMatch[1], bracketMatch[2]]
      : parenMatch
        ? [parenMatch[1], parenMatch[2]]
        : periodMatch
        ? [periodMatch[1], `[${periodMatch[2]}]`]
        : [text, ''];

    return { head, tail };
  };

  const renderMobileMarketLabel = (marketLabel) => {
    const { head, tail } = splitMarketLabel(marketLabel);

    return (
      <>
        <span className="zxsb_m_market_name_line">{head}</span>
        {tail && <span className="zxsb_m_market_name_line zxsb_m_market_name_line_sub">{tail}</span>}
      </>
    );
  };

  const renderResultBadgeLabel = (marketLabel) => {
    const { head, tail } = splitMarketLabel(marketLabel);

    return (
      <>
        <span className="zxsb_row_result_badge_line">{head}</span>
        {tail && (
          <span className="zxsb_row_result_badge_line zxsb_row_result_badge_line_sub">
            {tail}
          </span>
        )}
      </>
    );
  };

  const pageWindowStart = Math.max(1, Math.min(currentPage - 2, Math.max(totalPages - 4, 1)));
  const pageNumbers = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, index) => pageWindowStart + index,
  ).filter((page) => page <= totalPages);

  return (
    <>
      {/* 3-folder bonus bar */}
      {!isMobile && effectiveBonusTiers.length > 0 && (
        <div className="zxsb_folders">
          {effectiveBonusTiers.map(b => (
            <div key={b.fold} className={`zxsb_folder ${activeBonusFold === b.fold ? 'zxsb_folder_active' : ''}`}>
              <span>{b.fold}폴더</span>
              <span className="zxsb_folder_plus">⊕</span>
              <span className="zxsb_folder_val">x{Number(b.value).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Match list */}
      <div className="zxsb_matches">
        {loading && <SportsListSkeleton count={isMobile ? 4 : 5} mobile={isMobile} />}

        {groupedByLeague.map((group, gi) => (
          <div key={gi}>
            {/* League header: 국기 우선 → league icon → sport icon fallback */}
            <div className="zxsb_league">
              {(() => {
                const flagUrl = getCountryFlagUrl(group.league);
                if (flagUrl) return <img src={flagUrl} alt="" className="zxsb_league_flag" />;
                if (group.league.icon_url) return <img src={group.league.icon_url} alt="" className="zxsb_league_flag" />;
                return <span className="zxsb_league_icon">{getSportIcon(group.sport)}</span>;
              })()}
              <span>{(group.league?.countryInfo?.name_ko || group.league?.country || '')}</span>
              <span style={{ color: '#b0b8c8' }}>&gt;</span>
              <span>{group.league.name_ko || group.league.name}</span>
            </div>

            {/* Match rows */}
            {group.matches.map(m => {
              const { main, handicap, overunder, allMarkets } = getMarkets(m);
              const isSpecial = bettingType === 'SPECIAL';
              if (!isSpecial && !main && !handicap && !overunder && allMarkets.length === 0) return null;
              if (isSpecial && allMarkets.length === 0) return null;
              const mainOdds = main?.odds || main?.selections || [];
              const home = m.home_team_ko || m.home_team;
              const away = m.away_team_ko || m.away_team;
              const homeLogoUrl = resolveTeamLogoUrl(m, 'home');
              const awayLogoUrl = resolveTeamLogoUrl(m, 'away');
              const homeOdds = findMainOutcomeOdds(mainOdds, 'home');
              const drawOdds = findMainOutcomeOdds(mainOdds, 'draw');
              const awayOdds = findMainOutcomeOdds(mainOdds, 'away');
              const _plusN = m.extra_market_count ?? Math.max((allMarkets.length || 1) - 1, 0);

              // SPECIAL: allMarkets에서 display_order 순으로 정렬, 2-way subrow로 렌더
              const specialMarkets = isSpecial ? [...allMarkets].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999)) : [];
              const sportIconUrl = getSportIconUrl(group.sport);
              const mainMarketLabel = isSpecial
                ? '스페셜'
                : (buildMarketDisplayTitle(main) || '승무패');
              const handicapMarketLabel = buildMarketDisplayTitle(handicap) || '핸디캡';
              const overunderMarketLabel = buildMarketDisplayTitle(overunder) || '오버언더';
              const sportBadge = (
                <>
                  <span className="zxsb_m_market_badge_icon">
                    {sportIconUrl ? <img src={sportIconUrl} alt="" /> : getSportIcon(group.sport)}
                  </span>
                </>
              );

              const renderMobileMarketRow = ({ rowKey, marketLabel, cells, cellClass = 'zxsb_m_market_cells_3' }) => (
                <div key={rowKey} className="zxsb_m_market_row">
                  <div className="zxsb_m_market_badge">
                    {sportBadge}
                  </div>
                  <div className="zxsb_m_market_name">{renderMobileMarketLabel(marketLabel)}</div>
                  <div className={`zxsb_m_market_cells ${cellClass}`}>
                    {cells}
                  </div>
                </div>
              );

              if (isMobile) {
                const mobileRows = [];

                if (!isSpecial) {
                  mobileRows.push(renderMobileMarketRow({
                    rowKey: `main-${m.id}`,
                    marketLabel: mainMarketLabel,
                    cells: (
                      <>
                        {renderMobileOddsBtn(homeOdds, m, main, resolveSelectionLabel(homeOdds, m, main, '홈승'))}
                        {mainOdds.length >= 3
                          ? renderMobileOddsBtn(drawOdds, m, main, resolveSelectionLabel(drawOdds, m, main, '무'))
                          : renderMobileStaticCell('VS', 'zxsb_mo_static_vs')
                        }
                        {renderMobileOddsBtn(awayOdds, m, main, resolveSelectionLabel(awayOdds, m, main, '원정승'))}
                      </>
                    ),
                  }));
                }

                if (isSpecial && specialMarkets.length > 0) {
                  specialMarkets.forEach((mkt, mi) => {
                    const mktOdds = mkt.odds || mkt.selections || [];
                    if (mktOdds.length === 0) return;
                    const mktName = buildMarketDisplayTitle(mkt) || '마켓';
                    const mktLine = String(mkt.display_line || mkt.base_line || mkt.line || '').replace(/\s*\(0-0\)\s*$/, '');
                    if (mktOdds.length >= 3 && findMainOutcomeOdds(mktOdds, 'home') && findMainOutcomeOdds(mktOdds, 'draw')) {
                      const o1 = findMainOutcomeOdds(mktOdds, 'home');
                      const oX = findMainOutcomeOdds(mktOdds, 'draw');
                      const o2 = findMainOutcomeOdds(mktOdds, 'away');
                      mobileRows.push(renderMobileMarketRow({
                        rowKey: `special-3-${m.id}-${mi}`,
                        marketLabel: mktName,
                        cells: (
                          <>
                            {renderMobileOddsBtn(o1, m, mkt, resolveSelectionLabel(o1, m, mkt))}
                            {renderMobileOddsBtn(oX, m, mkt, resolveSelectionLabel(oX, m, mkt))}
                            {renderMobileOddsBtn(o2, m, mkt, resolveSelectionLabel(o2, m, mkt))}
                          </>
                        ),
                      }));
                      return;
                    }

                    const sel0 = mktOdds[0];
                    const sel1 = mktOdds[1];
                    mobileRows.push(renderMobileMarketRow({
                      rowKey: `special-2-${m.id}-${mi}`,
                      marketLabel: mktName,
                      cellClass: 'zxsb_m_market_cells_2',
                      cells: (
                        <>
                          {renderMobileOddsBtn(sel0, m, mkt, resolveSelectionLabel(sel0, m, mkt))}
                          {renderMobileStaticCell(mktLine || 'VS', mktLine ? 'zxsb_mo_static_line' : 'zxsb_mo_static_vs')}
                          {renderMobileOddsBtn(sel1, m, mkt, resolveSelectionLabel(sel1, m, mkt))}
                        </>
                      ),
                    }));
                  });
                }

                if (!isSpecial && handicap && handicap.home_selection && handicap.away_selection) {
                  const rawHandicapLine = String(handicap.display_line || handicap.base_line || handicap.line || handicap.home_line || '');
                  const homeLine = m.is_live ? rawHandicapLine : rawHandicapLine.replace(/\s*\(0-0\)\s*$/, '');
                  const awayLine = invertLine(homeLine);
                  mobileRows.push(renderMobileMarketRow({
                    rowKey: `handicap-${m.id}`,
                    marketLabel: handicapMarketLabel,
                    cellClass: 'zxsb_m_market_cells_pair',
                    cells: (
                      <>
                        {renderMobileOddsBtn(handicap.home_selection, m, handicap, withHandicapLineSuffix(resolveSelectionLabel(handicap.home_selection, m, handicap, '홈'), homeLine))}
                        {renderMobileOddsBtn(handicap.away_selection, m, handicap, withHandicapLineSuffix(resolveSelectionLabel(handicap.away_selection, m, handicap, '원정'), awayLine))}
                      </>
                    ),
                  }));
                }

                if (!isSpecial && overunder && (overunder.over_selection || (overunder.odds && overunder.odds.length >= 2))) {
                  const overSelection = overunder.over_selection || overunder.odds[0];
                  const underSelection = overunder.under_selection || overunder.odds[1];
                  const overunderLine = String(overunder.display_line || overunder.base_line || overunder.line || '')
                    .replace(/\s*\(0-0\)\s*$/, '');
                  mobileRows.push(renderMobileMarketRow({
                    rowKey: `overunder-${m.id}`,
                    marketLabel: overunderMarketLabel,
                    cellClass: 'zxsb_m_market_cells_pair',
                    cells: (
                      <>
                        {renderMobileOddsBtn(overSelection, m, overunder, withOverUnderLineSuffix(resolveSelectionLabel(overSelection, m, overunder, '오버'), overunderLine))}
                        {renderMobileOddsBtn(underSelection, m, overunder, withOverUnderLineSuffix(resolveSelectionLabel(underSelection, m, overunder, '언더'), overunderLine))}
                      </>
                    ),
                  }));
                }

                if (mobileRows.length === 0) return null;

                return (
                  <div key={m.id} className="zxsb_match_group zxsb_match_group_mobile">
                    <div className="zxsb_mobile_match_head">
                      <div className="zxsb_mobile_match_head_main">
                        <div className="zxsb_mobile_match_teams">
                          <span className="zxsb_mobile_match_team">
                            <TeamLogo url={homeLogoUrl} name={home} className="zxsb_mobile_team_logo" />
                            <span className="zxsb_mobile_match_team_name">{home}</span>
                          </span>
                          <span className="zxsb_mobile_match_vs">vs</span>
                          <span className="zxsb_mobile_match_team">
                            <TeamLogo url={awayLogoUrl} name={away} className="zxsb_mobile_team_logo" />
                            <span className="zxsb_mobile_match_team_name">{away}</span>
                          </span>
                        </div>
                      </div>
                      <div className="zxsb_mobile_match_time">{m.is_live ? 'LIVE' : formatTime(m.start_time)}</div>
                    </div>
                    <div className="zxsb_m_market_rows">
                      {mobileRows}
                    </div>
                  </div>
                );
              }

              return (
                <div key={m.id} className="zxsb_match_group">
                  {/* Main row: 7-col grid */}
                  <div className="zxsb_row">
                    <div className="zxsb_row_badge">
                      <span className="zxsb_row_sport_icon">{getSportIconUrl(group.sport) ? <img src={getSportIconUrl(group.sport)} alt="" /> : getSportIcon(group.sport)}</span>
                      <span className="zxsb_row_result_badge">
                        {renderResultBadgeLabel(mainMarketLabel)}
                      </span>
                    </div>
                    {isMobile && (
                      <div className="zxsb_mobile_market_chip">
                        {mainMarketLabel}
                      </div>
                    )}
                    <div className="zxsb_row_meta zxsb_row_meta_home">
                      <span className="zxsb_row_team_name">{home}</span>
                      <TeamLogo url={homeLogoUrl} name={home} className="zxsb_team_logo_sm" />
                    </div>
                    {isSpecial ? (
                      <>
                        <div className="zxsb_odds_btn zxsb_odds_empty"><span className="zxsb_odds_val">-</span></div>
                        <div className="zxsb_odds_btn zxsb_odds_empty"><span className="zxsb_odds_val">VS</span></div>
                        <div className="zxsb_odds_btn zxsb_odds_empty"><span className="zxsb_odds_val">-</span></div>
                      </>
                    ) : (
                      <>
                        {renderOddsBtn(homeOdds, m, main, resolveSelectionLabel(homeOdds, m, main, '홈승'))}
                        {mainOdds.length >= 3
                          ? renderOddsBtn(drawOdds, m, main, resolveSelectionLabel(drawOdds, m, main, '무'))
                          : <div className="zxsb_odds_btn zxsb_odds_empty"><span className="zxsb_odds_val">VS</span></div>
                        }
                        {renderOddsBtn(awayOdds, m, main, resolveSelectionLabel(awayOdds, m, main, '원정승'))}
                      </>
                    )}
                    <div className="zxsb_row_meta zxsb_row_meta_away">
                      <TeamLogo url={awayLogoUrl} name={away} className="zxsb_team_logo_sm" />
                      <span className="zxsb_row_team_name">{away}</span>
                    </div>
                    <div className="zxsb_row_end">
                      <span className="zxsb_row_time_text">
                        {m.is_live ? <span className="zxsb_row_live_tag">LIVE</span> : formatTime(m.start_time)}
                      </span>
                    </div>
                  </div>

                  {/* SPECIAL sub-rows: allMarkets display_order 순 */}
                  {isSpecial && specialMarkets.length > 0 && (
                    <div className="zxsb_subrows">
                      {specialMarkets.map((mkt, mi) => {
                        const mktOdds = mkt.odds || mkt.selections || [];
                        if (mktOdds.length === 0) return null;
                        const mktName = buildMarketDisplayTitle(mkt) || '마켓';
                        const mktLine = mkt.display_line || mkt.base_line || mkt.line || '';
                        // 3-way: 1/X/2
                        if (mktOdds.length >= 3 && findMainOutcomeOdds(mktOdds, 'home') && findMainOutcomeOdds(mktOdds, 'draw')) {
                          const o1 = findMainOutcomeOdds(mktOdds, 'home');
                          const oX = findMainOutcomeOdds(mktOdds, 'draw');
                          const o2 = findMainOutcomeOdds(mktOdds, 'away');
                          return (
                            <div key={mi} className="zxsb_subrow">
                              <div className="zxsb_row_badge">
                                <span className="zxsb_row_sport_icon">{getSportIconUrl(group.sport) ? <img src={getSportIconUrl(group.sport)} alt="" /> : getSportIcon(group.sport)}</span>
                                <span className="zxsb_row_result_badge" style={{ fontSize: '9px' }}>
                                  {renderResultBadgeLabel(mktName)}
                                </span>
                              </div>
                              {isMobile && <div className="zxsb_mobile_market_chip">{mktName}</div>}
                              <div />
                              {renderOddsBtn(o1, m, mkt, resolveSelectionLabel(o1, m, mkt))}
                              {renderOddsBtn(oX, m, mkt, resolveSelectionLabel(oX, m, mkt))}
                              {renderOddsBtn(o2, m, mkt, resolveSelectionLabel(o2, m, mkt))}
                              <div />
                              <div />
                            </div>
                          );
                        }
                        // 2-way: home/away or over/under or odd/even
                        const sel0 = mktOdds[0];
                        const sel1 = mktOdds[1];
                        const lbl0 = resolveSelectionLabel(sel0, m, mkt);
                        const lbl1 = resolveSelectionLabel(sel1, m, mkt);
                        return (
                          <div key={mi} className="zxsb_subrow">
                            <div className="zxsb_row_badge">
                              <span className="zxsb_row_sport_icon">{getSportIconUrl(group.sport) ? <img src={getSportIconUrl(group.sport)} alt="" /> : getSportIcon(group.sport)}</span>
                              <span className="zxsb_row_result_badge" style={{ fontSize: '9px' }}>
                                {renderResultBadgeLabel(mktName)}
                              </span>
                            </div>
                            {isMobile && <div className="zxsb_mobile_market_chip">{mktName}</div>}
                            <div />
                            {renderOddsBtn(sel0, m, mkt, lbl0)}
                            <span className="zxsb_subrow_line">{mktLine}</span>
                            {renderOddsBtn(sel1, m, mkt, lbl1)}
                            <div />
                            <div />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* DOMESTIC sub-rows: handicap + over/under */}
                  {!isSpecial && (handicap || overunder) && (
                    <div className="zxsb_subrows">
                      {handicap && handicap.home_selection && handicap.away_selection && (() => {
                        const h0 = handicap.home_selection;
                        const h1 = handicap.away_selection;
                        const rawLine = String(handicap.display_line || handicap.base_line || handicap.line || handicap.home_line || '');
                        const homeLine = m.is_live ? rawLine : rawLine.replace(/\s*\(0-0\)\s*$/, '');
                        const awayLine = invertLine(homeLine);
                        return (
                          <div className="zxsb_subrow zxsb_subrow_pair">
                            <div className="zxsb_row_badge">
                              <span className="zxsb_row_sport_icon">{getSportIconUrl(group.sport) ? <img src={getSportIconUrl(group.sport)} alt="" /> : getSportIcon(group.sport)}</span>
                              <span className="zxsb_row_result_badge">
                                {renderResultBadgeLabel(handicapMarketLabel)}
                              </span>
                            </div>
                            {renderOddsBtn(h0, m, handicap, withHandicapLineSuffix(resolveSelectionLabel(h0, m, handicap, '홈'), homeLine))}
                            {renderOddsBtn(h1, m, handicap, withHandicapLineSuffix(resolveSelectionLabel(h1, m, handicap, '원정'), awayLine))}
                          </div>
                        );
                      })()}
                      {overunder && (overunder.over_selection || (overunder.odds && overunder.odds.length >= 2)) && (() => {
                        const o0 = overunder.over_selection || overunder.odds[0];
                        const o1 = overunder.under_selection || overunder.odds[1];
                        const ouLine = String(overunder.display_line || overunder.base_line || overunder.line || '');
                        return (
                          <div className="zxsb_subrow zxsb_subrow_pair">
                            <div className="zxsb_row_badge">
                              <span className="zxsb_row_sport_icon">{getSportIconUrl(group.sport) ? <img src={getSportIconUrl(group.sport)} alt="" /> : getSportIcon(group.sport)}</span>
                              <span className="zxsb_row_result_badge">
                                {renderResultBadgeLabel(overunderMarketLabel)}
                              </span>
                            </div>
                            {renderOddsBtn(o0, m, overunder, withOverUnderLineSuffix(resolveSelectionLabel(o0, m, overunder, '오버'), ouLine))}
                            {renderOddsBtn(o1, m, overunder, withOverUnderLineSuffix(resolveSelectionLabel(o1, m, overunder, '언더'), ouLine))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {!loading && matches.length === 0 && (
          <div className="zxsb_empty">경기가 없습니다</div>
        )}
      </div>

      {/* Pagination */}
      {!loading && matchTotal > 0 && (
        <div className="zxsb_paging">
          <button type="button" className="zxsb_pg_btn" disabled={!hasPrevPage}
            onClick={() => goToPage(currentPage - 1)}>{isMobile ? '←' : '← 이전'}</button>
          {pageNumbers.map(p => (
            <button key={p} type="button"
              className={`zxsb_pg_btn ${currentPage === p ? 'zxsb_pg_active' : ''}`}
              onClick={() => goToPage(p)}>{p}</button>
          ))}
          <button type="button" className="zxsb_pg_btn" disabled={!hasMoreMatches}
            onClick={() => goToPage(currentPage + 1)}>{isMobile ? '→' : '다음 →'}</button>
        </div>
      )}
    </>
  );
};

export default FigmaDomesticBoard;
